import { toDecimal } from './money';

/**
 * ตรวจจับต้นทุนที่ "น่าจะกรอกผิดหน่วย" โดยเทียบกับราคาตลาด
 *
 * ## ทำไมต้องมี
 *
 * จากการใช้งานจริง เจ้าของกรอกต้นทุนผิดหน่วย 6 ครั้งจาก 16 (ดู PLAN.md B1) สองแบบ:
 *
 *   1. ใส่ **ราคาตลาดปัจจุบัน** แทนต้นทุนที่ซื้อมาจริง → กำไรกลายเป็น 0.00% เป๊ะ
 *   2. ใส่ **มูลค่ารวมเป็นดอลลาร์** แทนราคาต่อหน่วย → LLY กลายเป็น +725%
 *
 * ทั้งสองแบบระบบรับได้หมดเพราะเป็นตัวเลขบวกที่ถูกต้องตามรูปแบบ ต้องดูบริบทถึงจะรู้
 *
 * ## เป็นคำเตือน ไม่ใช่การบล็อก
 *
 * ทั้งสองกรณีอาจถูกต้องจริงก็ได้ — เพิ่งซื้อวันนี้ที่ราคาตลาดพอดี หรือถือ 1 หน่วยพอดี
 * จึงแค่ชี้ให้ดู ไม่ห้ามบันทึก การบล็อกของที่ถูกต้องน่ารำคาญกว่าการเตือนของที่ถูก
 */

export type CostWarning =
  /** ต้นทุนเท่ากับราคาตลาดพอดี — น่าจะกรอกราคาปัจจุบันแทนต้นทุน */
  | { kind: 'equals-market-price' }
  /** ต้นทุนใกล้เคียงมูลค่ารวม — น่าจะกรอกมูลค่าแทนราคาต่อหน่วย */
  | { kind: 'looks-like-total-value'; totalValue: string; impliedUnitCost: string };

/** ต้นทุนต่างจากมูลค่ารวมไม่เกินเท่านี้ ถือว่าน่าสงสัยว่ากรอกมูลค่ามาแทน */
const TOTAL_VALUE_TOLERANCE = 0.05;

export function checkCostAgainstMarket(input: {
  quantity: string;
  avgCost: string;
  marketPrice: string;
}): CostWarning | null {
  let quantity, avgCost, marketPrice;
  try {
    quantity = toDecimal(input.quantity, 'quantity');
    avgCost = toDecimal(input.avgCost, 'avgCost');
    marketPrice = toDecimal(input.marketPrice, 'marketPrice');
  } catch {
    // ยังกรอกไม่ครบหรือกรอกไม่ถูกรูปแบบ — ยังไม่ถึงเวลาเตือนเรื่องหน่วย
    return null;
  }

  // ต้นทุนศูนย์เป็นเรื่องปกติ (airdrop, ของแถม) และหารไม่ได้
  if (avgCost.isZero() || avgCost.isNegative() || marketPrice.isZero() || quantity.isZero()) {
    return null;
  }

  if (avgCost.equals(marketPrice)) {
    return { kind: 'equals-market-price' };
  }

  const totalValue = quantity.mul(marketPrice);
  const gap = totalValue.minus(avgCost).abs().div(avgCost);

  if (gap.lessThan(TOTAL_VALUE_TOLERANCE)) {
    return {
      kind: 'looks-like-total-value',
      totalValue: totalValue.toFixed(),
      // ราคาต่อหน่วยที่น่าจะตั้งใจกรอก ถ้าเลขที่ใส่มาคือมูลค่ารวมจริง
      impliedUnitCost: avgCost.div(quantity).toFixed(),
    };
  }

  return null;
}
